/**
 * User-selectable tool surfaces and their permission expectations.
 *
 * Power Automate flow access is the base feature because setup, environment
 * selection, and the core MCP value all rely on the Flow Service audience.
 */

export const FEATURE_IDS = [
  "powerAutomate",
  "graph",
  "powerApps",
  "dataverse",
  "admin",
  "powerPages",
  "powerPagesManagement",
] as const;

export type FeatureId = typeof FEATURE_IDS[number];

export const DEFAULT_FEATURES: FeatureId[] = [...FEATURE_IDS];
export const MINIMAL_FEATURES: FeatureId[] = ["powerAutomate"];

export const FEATURE_LABELS: Record<FeatureId, string> = {
  powerAutomate: "Power Automate flows",
  graph: "SharePoint, OneDrive, and Excel helpers",
  powerApps: "Connections, connector catalog, Power Apps, and Canvas source authoring",
  dataverse: "Dataverse tables, rows, solutions, desktop flows (RPA), work queues, and approvals",
  admin: "Power Platform admin and solution ALM",
  powerPages: "Power Pages configuration",
  powerPagesManagement: "Power Pages hosting management",
};

export const FEATURE_DESCRIPTIONS: Record<FeatureId, string> = {
  powerAutomate: "Flow listing, authoring, runs, sharing, approvals, and environment selection.",
  graph: "Microsoft Graph access for SharePoint sites/lists/files, OneDrive, and Excel file helpers.",
  powerApps: "PowerApps Service and Power Platform API access for connections (including creating, testing, and repairing them), connectors, and Power Apps, plus optional .NET 10 Canvas Authoring MCP preview support.",
  dataverse: "Dynamics CRM plus BAP access for Dataverse URL discovery and Dataverse-backed tools, including desktop flows (RPA), machines, and work queues.",
  admin: "BAP Admin API access for environment lifecycle, DLP, capacity, billing, and solution ALM tools.",
  powerPages: "Power Pages site configuration stored in Dataverse.",
  powerPagesManagement: "Power Platform API access for Power Pages hosting operations such as provision, delete, and restart.",
};

const FEATURE_ORDER = new Map<FeatureId, number>(FEATURE_IDS.map((id, index) => [id, index]));

export function isFeatureId(value: string): value is FeatureId {
  return (FEATURE_IDS as readonly string[]).includes(value);
}

export function normalizeFeatures(input: readonly FeatureId[] | undefined | null): FeatureId[] {
  const selected = new Set<FeatureId>(input ?? DEFAULT_FEATURES);

  // The Flow Service is the base audience for setup and the core server.
  selected.add("powerAutomate");

  // Power Pages configuration is Dataverse-backed.
  if (selected.has("powerPages")) {
    selected.add("dataverse");
  }

  // Power Pages hosting management needs Dataverse context and the BAP admin API.
  if (selected.has("powerPagesManagement")) {
    selected.add("powerPages");
    selected.add("dataverse");
    selected.add("admin");
  }

  return [...selected].sort((a, b) => (FEATURE_ORDER.get(a) ?? 0) - (FEATURE_ORDER.get(b) ?? 0));
}

export function hasFeature(enabled: readonly FeatureId[] | undefined, feature: FeatureId): boolean {
  return normalizeFeatures(enabled).includes(feature);
}

export function formatFeatureList(features: readonly FeatureId[]): string {
  return normalizeFeatures(features).map((feature) => FEATURE_LABELS[feature]).join(", ");
}
